// Parent → Children panel. The fleshed-out screen of R4: list kids, add a kid
// (username availability + password + avatar + grade), and per-kid actions —
// open-as (quick-launch), edit, reset password, remove.

import { useT } from '@fajrarabi/i18n/react';
import { AVATAR_CATALOG, Alert, Button, Card, CardBody, Skeleton } from '@fajrarabi/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate, useParams } from '@tanstack/react-router';
import { useEffect, useState } from 'react';

import { KidFormModal } from '~/components/kids/KidFormModal';
import { ResetPasswordModal } from '~/components/kids/ResetPasswordModal';
import { ApiError } from '~/lib/api';
import { useAuth } from '~/lib/auth';
import { deleteKid, listKids, quickLaunchKid, type Kid } from '~/lib/family';
import { asLocale } from '~/lib/locale';
import { shellForContext } from '~/lib/roles';

export const Route = createFileRoute('/$locale/kids')({
  component: KidsPanel,
});

const MAX_KIDS = 3;

function KidsPanel() {
  const t = useT();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { locale: rawLocale } = useParams({ from: '/$locale/kids' });
  const locale = asLocale(rawLocale);
  const { currentRoleContext } = useAuth();

  // Parent-only screen. A non-parent (e.g. a kid after quick-launch, which
  // reloads on the same /kids URL) is bounced to their role home so they
  // never see family-management content inside the wrong shell.
  const isParent = shellForContext(currentRoleContext) === 'parent';
  useEffect(() => {
    if (!isParent) {
      void navigate({ to: '/$locale', params: { locale }, replace: true });
    }
  }, [isParent, navigate, locale]);

  const kidsQuery = useQuery({
    queryKey: ['family', 'kids'],
    queryFn: ({ signal }) => listKids(signal),
    enabled: isParent,
  });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Kid | null>(null);
  const [resetting, setResetting] = useState<Kid | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);

  const quickLaunch = useMutation({
    mutationFn: (kid: Kid) => quickLaunchKid(kid.id),
    onSuccess: () => {
      // The quick-launch response set the kid's session cookie. Hard-navigate
      // to the locale home so the app re-boots as the kid on the kid home —
      // not on this parent-only /kids URL.
      window.location.assign(`/${locale}`);
    },
    onError: (err) =>
      setActionError(err instanceof ApiError ? err.message : t('app.common.error_generic')),
  });

  const remove = useMutation({
    mutationFn: (kid: Kid) => deleteKid(kid.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['family', 'kids'] }),
    onError: (err) =>
      setActionError(err instanceof ApiError ? err.message : t('app.common.error_generic')),
  });

  const kids = kidsQuery.data ?? [];
  const atCap = kids.length >= MAX_KIDS;

  function openAdd() {
    setEditing(null);
    setFormOpen(true);
  }
  function openEdit(kid: Kid) {
    setEditing(kid);
    setFormOpen(true);
  }
  function confirmRemove(kid: Kid) {
    const name = kid.displayName ?? kid.username;
    if (window.confirm(t('app.parent.delete_kid_confirm', { name }))) {
      setActionError(null);
      remove.mutate(kid);
    }
  }

  // Non-parents are mid-redirect (see the effect above) — render nothing so
  // family content never flashes inside another role's shell.
  if (!isParent) return null;

  return (
    <div className="mx-auto max-w-3xl px-6 py-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-ink-900">{t('app.parent.kids_title')}</h1>
          <p className="mt-1 text-ink-700">{t('app.parent.kids_subtitle')}</p>
        </div>
        <Button onClick={openAdd} disabled={atCap}>
          {t('app.parent.add_kid')}
        </Button>
      </div>

      <p className="mt-2 text-xs text-ink-500">{t('app.parent.kid_cap_note')}</p>

      {actionError && (
        <Alert tone="danger" className="mt-4">
          {actionError}
        </Alert>
      )}

      <div className="mt-6 space-y-3">
        {kidsQuery.isLoading && (
          <>
            <Skeleton className="h-24 rounded-3xl" />
            <Skeleton className="h-24 rounded-3xl" />
          </>
        )}

        {!kidsQuery.isLoading && kids.length === 0 && (
          <Card>
            <CardBody>
              <p className="text-sm text-ink-700">{t('app.parent.no_kids')}</p>
            </CardBody>
          </Card>
        )}

        {kids.map((kid) => (
          <KidRow
            key={kid.id}
            kid={kid}
            launching={quickLaunch.isPending && quickLaunch.variables?.id === kid.id}
            onOpenAs={() => {
              setActionError(null);
              quickLaunch.mutate(kid);
            }}
            onEdit={() => openEdit(kid)}
            onReset={() => setResetting(kid)}
            onRemove={() => confirmRemove(kid)}
          />
        ))}
      </div>

      <KidFormModal open={formOpen} onOpenChange={setFormOpen} kid={editing} />
      <ResetPasswordModal
        open={resetting !== null}
        onOpenChange={(o) => !o && setResetting(null)}
        kid={resetting}
      />
    </div>
  );
}

function KidRow({
  kid,
  launching,
  onOpenAs,
  onEdit,
  onReset,
  onRemove,
}: {
  kid: Kid;
  launching: boolean;
  onOpenAs: () => void;
  onEdit: () => void;
  onReset: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  const name = kid.displayName ?? kid.username;
  const emoji = AVATAR_CATALOG.find((a) => a.slug === kid.avatar)?.emoji ?? '🙂';

  return (
    <Card>
      <CardBody>
        <div className="flex flex-wrap items-center gap-4">
          <span
            className="grid h-14 w-14 shrink-0 place-items-center rounded-2xl bg-cream-100 text-3xl"
            aria-hidden="true"
          >
            {emoji}
          </span>
          <div className="min-w-0 flex-1">
            <p className="truncate text-lg font-bold text-ink-900">{name}</p>
            <p className="truncate text-sm text-ink-500">
              <bdi>@{kid.username}</bdi>
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" loading={launching} onClick={onOpenAs}>
              {t('app.parent.open_as', { name })}
            </Button>
            <Button size="sm" variant="secondary" onClick={onEdit}>
              {t('app.parent.edit_kid')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onReset}>
              {t('app.parent.reset_password')}
            </Button>
            <Button size="sm" variant="ghost" onClick={onRemove}>
              {t('app.parent.delete_kid')}
            </Button>
          </div>
        </div>
      </CardBody>
    </Card>
  );
}
